"use client";

let registered = false;

/** Как часто спрашивать сервер о новой версии SW, пока вкладка открыта. */
const UPDATE_CHECK_INTERVAL_MS = 15 * 60 * 1000;
/** Сколько вкладка должна простоять без действий пользователя, чтобы её можно было перезагрузить на виду. */
const IDLE_BEFORE_RELOAD_MS = 5 * 60 * 1000;

const ACTIVITY_EVENTS = [
  "pointerdown",
  "keydown",
  "wheel",
  "touchstart",
] as const;

export function registerServiceWorker() {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;

  if (process.env.NODE_ENV !== "production") {
    unregisterAll();
    return;
  }

  if (registered) return;
  registered = true;

  const doRegister = () => {
    // Обновление применяется само, без вопросов пользователю (KAN-914: тост
    // "Доступно обновление" раздражал сильнее, чем помогал). Но перезагрузка
    // страницы делается не в лоб, а в безопасный момент: когда вкладка скрыта
    // либо давно простаивает и в полях нет несохранённого текста. Так и старая
    // жалоба "сайт перезагружается сам посреди работы" не возвращается.
    let pendingWorker: ServiceWorker | null = null;
    let reloadScheduled = false;
    let reloading = false;
    let idleTimer: ReturnType<typeof setTimeout> | null = null;
    // Первая установка SW тоже стреляет controllerchange (из-за clients.claim
    // в активации) — это не деплой, перезагружать вкладку не за чем.
    let hadController = Boolean(navigator.serviceWorker.controller);

    const reloadNow = () => {
      if (reloading) return;
      reloading = true;
      window.location.reload();
    };

    const hasUnsavedInput = () => {
      const fields = document.querySelectorAll<
        HTMLInputElement | HTMLTextAreaElement
      >("input, textarea");
      for (const field of fields) {
        if (field.type === "password") continue;
        if (field.value && field.value !== field.defaultValue) return true;
      }
      const editables = document.querySelectorAll<HTMLElement>(
        "[contenteditable='true'], [contenteditable='']",
      );
      for (const editable of editables) {
        if ((editable.textContent ?? "").trim().length > 0) return true;
      }
      return false;
    };

    const tryApply = () => {
      if (reloading) return;
      if (!document.hidden && hasUnsavedInput()) {
        // Пользователь что-то печатает — попробуем ещё раз позже.
        resetIdleTimer();
        return;
      }

      if (pendingWorker) {
        // SKIP_WAITING переводит новый воркер в active; дальше сработает
        // controllerchange и мы перезагрузимся уже с новым кэшем.
        const worker = pendingWorker;
        pendingWorker = null;
        worker.postMessage({ type: "SKIP_WAITING" });
        return;
      }

      // Контроллер уже сменился (например, обновление применила соседняя
      // вкладка) — этой вкладке остаётся только перечитать страницу.
      reloadNow();
    };

    const resetIdleTimer = () => {
      if (!reloadScheduled) return;
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(tryApply, IDLE_BEFORE_RELOAD_MS);
    };

    const scheduleUpdate = (worker: ServiceWorker | null) => {
      if (worker) pendingWorker = worker;

      if (!reloadScheduled) {
        reloadScheduled = true;
        document.addEventListener("visibilitychange", () => {
          if (document.hidden) tryApply();
        });
        ACTIVITY_EVENTS.forEach((type) =>
          window.addEventListener(type, resetIdleTimer, { passive: true }),
        );
      }

      resetIdleTimer();
      // Вкладка уже в фоне — обновляемся прямо сейчас, пользователь ничего не заметит.
      if (document.hidden) tryApply();
    };

    navigator.serviceWorker
      .register("/sw.js", { scope: "/", updateViaCache: "none" })
      .then((registration) => {
        // К моменту регистрации новый воркер мог уже дождаться своей очереди
        // (обновление скачалось, пока вкладка висела на другой странице).
        if (registration.waiting && navigator.serviceWorker.controller) {
          scheduleUpdate(registration.waiting);
        }

        registration.addEventListener("updatefound", () => {
          const newWorker = registration.installing;
          if (!newWorker) return;

          newWorker.addEventListener("statechange", () => {
            // "installed" при уже существующем controller — это обновление.
            // Без controller это первая установка SW, перезагружать нечего.
            if (
              newWorker.state === "installed" &&
              navigator.serviceWorker.controller
            ) {
              scheduleUpdate(newWorker);
            }
          });
        });

        // Браузер сам проверяет sw.js только при навигации. Долгоживущая
        // SPA-вкладка без этого узнала бы о деплое лишь при F5.
        const checkForUpdate = () => {
          registration.update().catch(() => {});
        };
        setInterval(checkForUpdate, UPDATE_CHECK_INTERVAL_MS);
        document.addEventListener("visibilitychange", () => {
          if (!document.hidden) checkForUpdate();
        });
      })
      .catch((err) => {
        console.error("[SW] Registration failed:", err);
        registered = false;
      });

    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (!hadController) {
        hadController = true;
        return;
      }
      if (pendingWorker === null && reloadScheduled) {
        // Это наш собственный SKIP_WAITING — перезагружаемся сразу.
        reloadNow();
        return;
      }
      // Контроллер сменила другая вкладка: старые чанки этой сборки уже
      // выброшены из кэша, поэтому вкладку тоже нужно обновить — но в
      // безопасный момент, а не под руками у пользователя.
      scheduleUpdate(null);
    });
  };

  if (document.readyState === "complete") {
    doRegister();
  } else {
    window.addEventListener("load", doRegister, { once: true });
  }
}

export function cleanServiceWorkerCache(): Promise<boolean> {
  return new Promise((resolve) => {
    if (
      !("serviceWorker" in navigator) ||
      !navigator.serviceWorker.controller
    ) {
      resolve(false);
      return;
    }

    const timeout = setTimeout(() => resolve(false), 5000);
    const channel = new MessageChannel();

    channel.port1.onmessage = (event) => {
      clearTimeout(timeout);
      resolve(event.data?.success === true);
    };

    navigator.serviceWorker.controller.postMessage(
      { type: "CLEAN_CACHE" },
      [channel.port2],
    );
  });
}

function unregisterAll() {
  try {
    navigator.serviceWorker
      .getRegistrations()
      .then((regs) => regs.forEach((r) => r.unregister().catch(() => {})))
      .catch(() => {});

    if ("caches" in window) {
      caches
        .keys()
        .then((keys) =>
          Promise.all(keys.map((k) => caches.delete(k).catch(() => {}))),
        )
        .catch(() => {});
    }
  } catch {
    // ignore
  }
}
